import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useLocation, useNavigate, useParams, Link } from "react-router-dom";
import {
	Box,
	Button,
	Checkbox,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	FormControl,
    Divider,
	FormControlLabel,
	FormGroup,
	Menu,
	MenuItem,
	Radio,
	RadioGroup,
	Select,
	Stack,
	TextField,
	Typography,	
    Alert
} from "@mui/material";
import { ApiRequestFailed, authenticateAndFetchData, authenticateAndPostData, retryFn } from "@lib/apis";
import PageLoading from "@components/PageLoading";
import PageDataErrorHandler from "@components/PageDataErrorHandler";
import { ColDef, ValueFormatterParams } from "ag-grid-community";
import { CustomCellRendererProps } from "ag-grid-react";
import { Add, CloudUpload, Delete, Downloading, ArrowBack, Edit, MoreVert, Update } from "@mui/icons-material";
import PaginatedServerSideDataTable from "@components/PaginatedServerSideDataTable";
import { urls } from "@routes";
import IconButton from "@mui/material/IconButton";


interface Contact {
	uid: string;
	email: string;
	bounced: boolean;
	unsubscribed: boolean;
	[key: string]: any;
}

interface PaginationData {
	data: Contact[];
	total_count: number;
	total_pages: number;
	current_page: number;
	page_size: number;
}

interface ApiResponseData {
	list_name: string;
	contacts: Contact[];
	attribute_column_headers: string[];
	total_count: number;
	total_pages: number;
	current_page: number;
	page_size: number;
}

export default function AdminContactListDetails() {
	const { contactListUID, userId } = useParams();
	const navigate = useNavigate();

	const [pageData, setPageData] = useState<PaginationData>();
	const [listName, setListName] = useState("");
	const [additionalColumnNames, setAdditionalColumnNames] = useState<string[]>([]);

	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(50);
	const [search, setSearch] = useState("");

	const pageDataQuery = useQuery({
		queryKey: [
			"adminContactListDetails",
			contactListUID,
			page,
			pageSize,
			search
		],
		queryFn: () =>
			authenticateAndFetchData(
				`/admin/contact-list-details/?uid=${contactListUID}&page=${page}&page_size=${pageSize}&search=${search}`
			),
		gcTime: 0,
		retry: retryFn,
		refetchOnWindowFocus: false,
	});

	useEffect(() => {
		if (pageDataQuery.data?.data) {
			const data = pageDataQuery.data.data as ApiResponseData;

			setPageData({
				data: data.contacts || [],
				total_count: data.total_count,
				total_pages: data.total_pages,
				current_page: data.current_page,
				page_size: data.page_size,
			});

			setListName(data.list_name);

			const filteredColumns =
				data.attribute_column_headers?.filter(
					value => value !== "Email Address"
				) || [];

			setAdditionalColumnNames(filteredColumns);

			console.log("FILTERED COLUMNS:", filteredColumns);

		}
	}, [pageDataQuery.data]);

	const handlePageChange = (newPage: number) => {
		setPage(newPage);
	};

	const handlePageSizeChange = (newPageSize: number) => {
		setPageSize(newPageSize);
		setPage(1);
	};

	const handleSearchChange = (value: string) => {
		setSearch(value);
		setPage(1);
	};

	const bounceValueFormatter = (customProps: CustomCellRendererProps) => {
		return customProps.data["bounced"] ? (
			<Typography color="error">Yes</Typography>
		) : (
			<Typography color="success">No</Typography>
		);
	};

	const unsubscribedValueFormatter = (
		customProps: CustomCellRendererProps
	) => {
		return customProps.data["unsubscribed"] ? (
			<Typography color="error">Yes</Typography>
		) : (
			<Typography color="success">No</Typography>
		);
	};

	const colDefs: ColDef[] = useMemo(() => {
		const columns: ColDef[] = [
			{
				field: "email",
				headerName: "Contact Email ID",
				flex: 2
			},
			{
				field: "uid",
				headerName: "Contact UID",
				flex: 2
			}
		];

		additionalColumnNames.slice(0, 4).forEach(columnName => {
			columns.push({
				field: columnName,
				headerName: columnName,
				flex: 1.5
			});
		});

		columns.push({
			field: "bounced",
			headerName: "Bounced",
			cellRenderer: bounceValueFormatter,
			sortable: false
		});

		columns.push({
			field: "unsubscribed",
			headerName: "Unsubscribed",
			cellRenderer: unsubscribedValueFormatter,
			sortable: false
		});

		return columns;
	}, [additionalColumnNames]);

	if (!pageData && pageDataQuery.isLoading) {
		return <PageLoading />;
	}

	if (pageDataQuery.error as unknown as ApiRequestFailed) {
		return (
			<PageDataErrorHandler
				error={pageDataQuery.error as unknown as ApiRequestFailed}
			/>
		);
	}

	return (
		<Stack direction="column" spacing={4} sx={{ pb: 4, height: "100%" }}>
			<Stack direction="row" spacing={2} alignItems="center">
				<IconButton
					onClick={() =>
						navigate(
							urls["adminUserContactList"].replace(
								":userId",
								String(userId)
							)
						)
					}
				>
					<ArrowBack />
				</IconButton>

				<Typography variant="h6">
					Contact List: {listName}
				</Typography>
			</Stack>

			<Divider />

			<Alert severity="info">
				Upto 20 columns are imported from your CSV.
				However, only first 5 columns can be shown below.
			</Alert>

			<Box sx={{ height: "100%" }}>
				<PaginatedServerSideDataTable
					key={additionalColumnNames.join("-")}
					columns={colDefs}
					paginationData={pageData}
					loading={pageDataQuery.isFetching}
					onPageChange={handlePageChange}
					onPageSizeChange={handlePageSizeChange}
					onSearchChange={handleSearchChange}
					searchPlaceholder="Search by email..."
					noRowsText="No Contacts Available"
				/>
			</Box>
		</Stack>
	);
}
